import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SearchResultItem } from '@internal/schema-client';
import { NavMenu } from '@internal/schema-client';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types/prop-types';
import {
  Input,
  Label,
  Modal,
  Text,
  Title
} from '@gjensidige/builders-components';
import { Search } from '@gjensidige/builders-icons';
import { trackEvent } from '@gjensidige/core-analytics/lib/track-event';
import { TrackingContext } from '@gjensidige/core-analytics/lib/tracking-context';
import { TrackEventConfig } from '@gjensidige/core-analytics/lib/types';
import { Nav } from '@gjensidige/core-navigation';

type States = 'idle' | 'loading' | Array<SearchResultItem>;

const DEFAULT_TRACKING: TrackEventConfig = {
  eventCategory: 'input',
  eventLabel: 'site-search',
  eventKey: 'input'
};

import styles from '../less/search-modal.module.less';

export const SearchModal = ({
  action,
  messages,
  onClickModal,
  searchAPIUrl,
  showModal,
  title
}: NavMenu['search']) => {
  const { options: trackingOptions } = useContext(TrackingContext);
  const texts = useContext(Nav.language);
  const searchRef = useRef<HTMLInputElement>(null);
  const controllerRef = useRef<AbortController | null>();
  const hideSearchIcon = searchRef?.current?.value?.length > 0;

  const [value, setValue] = useState('');
  const [state, setState] = useState<States>('idle');

  const debouncedSearch = useRef(
    debounce(async (query) => {
      if (controllerRef.current) controllerRef.current.abort();

      const controller = new AbortController();
      controllerRef.current = controller;
      const res = await fetch(searchAPIUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
        signal: controllerRef.current?.signal
      });
      const data = await res.json();
      controllerRef.current = null;
      setState(data);
    }, 2000)
  ).current;

  useEffect(() => {
    searchRef?.current?.focus(); // Allow react to render before setting focus
  }, [showModal]);
  useEffect(() => {
    const shouldSearch = value.length >= 3;
    setState(shouldSearch ? 'loading' : 'idle');
    if (shouldSearch) debouncedSearch(value);
  }, [value]);

  useEffect(() => {
    if (!Array.isArray(state)) return;
    if (state?.length) {
      trackEvent(
        {
          ...DEFAULT_TRACKING,
          eventLabel: value,
          eventCategory: 'site-search',
          eventAction: 'search',
          eventStatus: 'success'
        },
        trackingOptions
      );
    } else if (value.length >= 3) {
      trackEvent(
        {
          eventLabel: value,
          eventCategory: 'site-search',
          eventAction: 'search',
          eventStatus: 'error'
        },
        trackingOptions
      );
    }
  }, [state]);

  const label = title || texts.search || 'Search';
  const nrOfHits =
    state?.length === 1
      ? messages.result?.hit
      : messages.result?.hits
      ? messages.result?.hits.replace('!!', state?.length.toString())
      : 'hits';

  return createPortal(
    <Modal
      aria-label={label}
      className={styles.dialog}
      closeLabel={texts.close || 'Close'}
      onClose={onClickModal}
      open={showModal}
    >
      <form method="get" action={action.href}>
        <Label>
          <Title as="span" size="5" text={label} className={styles.title} />
          <Input
            autoFocus={true}
            name="q"
            onChange={({ target }) => setValue(target.value)}
            placeholder={label}
            ref={searchRef}
            required
            suffix={!hideSearchIcon && <Search />}
            tracking={false}
            type="search"
            value={value}
          />
        </Label>
      </form>
      {state === 'loading' && <div className={styles.loading} />}
      {!Array.isArray(state) || !state.length || (
        <>
          <Title aria-live="polite" size="6" as="h2">
            {messages.result.title || 'Search results:'} <span>{nrOfHits}</span>
          </Title>
          <div className={styles.items}>
            {state.map(({ title, preface, link, _id }: SearchResultItem) => (
              <a href={link.url} className={styles.item} key={_id}>
                <Title as="h3" size="5" text={title} />
                {!preface || <Text size="small">{preface}</Text>}
                <Text size="small" className={styles.breadcrumb}>
                  {breadCrumbManipulator(link.url)}
                </Text>
              </a>
            ))}
          </div>
        </>
      )}
      {state.length === 0 && (
        <>
          <strong aria-live="polite">
            {messages.noResult?.title || 'No results'}
          </strong>
          <div>{messages.noResult?.preface || ''}</div>
        </>
      )}
      {state === 'idle' && (
        <>
          <strong aria-live="polite">
            {messages.information?.title || 'Looking for something special?'}
          </strong>
          <div>
            {messages.information?.preface || 'Start typing minimum 3 letters'}
          </div>
        </>
      )}
    </Modal>,
    document.body
  );
};

SearchModal.CSR = true;
SearchModal.propTypes = {
  info: PropTypes.any,
  noResult: PropTypes.any,
  onClickModal: PropTypes.func,
  placeholder: PropTypes.string,
  result: PropTypes.any,
  searchUrl: PropTypes.string,
  setShowModal: PropTypes.func,
  showModal: PropTypes.bool,
  title: PropTypes.string
};

export default SearchModal;

const breadCrumbManipulator = (target: string) =>
  target
    .slice(1)
    .replace(/-/gi, ' ')
    .replace(/\//gi, ' → ')
    .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter?.toUpperCase());
